import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SchedulerService from "../../services/SchedulerService";
import NotificationTemplateService from "../../services/NotificationTemplateService";
import HttpService from "../../services/HttpService";
import { useChosenEmployer } from "../../contexts/EmployerContext";

const formatEasternHour = (hourUtc) => {
  const date = new Date();
  const utcDate = new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      hourUtc
    )
  );
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "numeric",
    hourCycle: "h23",
    timeZoneName: "short",
  }).formatToParts(utcDate);
  return `${parts.find((part) => part.type === "hour").value}:00 ${parts.find((part) => part.type === "timeZoneName").value}`;
};

const formatEasternDateTime = (value) => {
  if (!value) return "—";

  const timestamp = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value)
    ? value
    : `${value}Z`;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  }).format(date);
};

const INSTANCE_STATUS_COLORS = {
  ACTIVE: "success",
  COMPLETED: "default",
  CANCELLED: "error",
  PAUSED: "warning",
  TRANSITIONED: "info",
};

const EXEC_STATUS_COLORS = {
  PENDING: "info",
  SENT: "success",
  FAILED: "error",
  SKIPPED: "warning",
  CANCELLED: "default",
};

const ALL_INSTANCE_STATUSES = [
  "ALL",
  "ACTIVE",
  "COMPLETED",
  "CANCELLED",
  "PAUSED",
  "TRANSITIONED",
];

export default function SchedulerInstances() {
  const { chosenEmployerId } = useChosenEmployer();
  const isAnyEmployer = chosenEmployerId === "ANY";

  const [searchParams, setSearchParams] = useSearchParams();
  const campaignIdFilter = searchParams.get("campaignId") || "";
  const employerIdFromUrl = searchParams.get("employerId") || "";

  const clearCampaignFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("campaignId");
    next.delete("employerId");
    setSearchParams(next);
  };

  const [selectedInstance, setSelectedInstance] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [previewProcess, setPreviewProcess] = useState(null); // idProcess string
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [memberIdSearch, setMemberIdSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // Reset to page 0 when any filter changes
  useEffect(() => {
    setPaginationModel((pm) => ({ ...pm, page: 0 }));
  }, [statusFilter, selectedEmployer, memberIdSearch, campaignIdFilter]);

  // ── Employers list ────────────────────────────────────────────────────
  const { data: employersData } = useQuery(
    ["employers"],
    () =>
      HttpService.getAxiosClient().get(
        window.API_URL + `/employer/all?excludeRelations=true`
      ),
    { staleTime: 5 * 60 * 1000 }
  );
  const employerOptions = useMemo(
    () =>
      (employersData?.data || []).map((e) => ({
        id: e.idEmployer ?? e.id_employer,
        label: e.name + " (" + (e.idEmployer ?? e.id_employer) + ")",
      })),
    [employersData]
  );

  // effective employer ID for the API call — dropdown always takes priority,
  // falling back to the employerId passed in from the campaign link
  const effectiveEmployerId =
    selectedEmployer?.id ??
    (employerIdFromUrl || (isAnyEmployer ? "ANY" : chosenEmployerId));

  // ── Instances ──────────────────────────────────────────────────────────
  const { data: instancesResponse, isLoading } = useQuery(
    [
      "scheduler-instances",
      effectiveEmployerId,
      statusFilter,
      memberIdSearch,
      campaignIdFilter,
      paginationModel.page,
      paginationModel.pageSize,
    ],
    () =>
      SchedulerService.getInstances({
        employerId: effectiveEmployerId,
        status: statusFilter !== "ALL" ? statusFilter : undefined,
        idEmployee: memberIdSearch.trim() || undefined,
        campaignId: campaignIdFilter || undefined,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      }),
    { select: (r) => r.data, keepPreviousData: true }
  );

  const instances = instancesResponse?.data || [];
  const instancesTotal = instancesResponse?.total ?? 0;

  // ── Executions for selected instance ──────────────────────────────────
  const { data: executions, isLoading: execLoading } = useQuery(
    ["scheduler-executions", selectedInstance?.idInstance],
    () => SchedulerService.getExecutions(selectedInstance.idInstance),
    {
      enabled: !!selectedInstance,
      select: (r) => r.data,
    }
  );

  // ── Task details via template days (uses already-deployed endpoints) ──
  const { data: templateDays } = useQuery(
    ["scheduler-days", selectedInstance?.idScheduleTemplate],
    () => SchedulerService.getDays(selectedInstance.idScheduleTemplate),
    { enabled: !!selectedInstance, select: (r) => r.data }
  );
  const dayTaskResults = useQueries({
    queries: (templateDays || []).map((day) => ({
      queryKey: ["scheduler-tasks-day", day.idScheduleDay],
      queryFn: () =>
        SchedulerService.getTasks(day.idScheduleDay).then((r) => r.data),
      staleTime: 5 * 60 * 1000,
    })),
  });
  const taskMap = useMemo(() => {
    const map = {};
    dayTaskResults.forEach(({ data }) => {
      (data || []).forEach((task) => {
        map[task.idScheduleTask] = task;
      });
    });
    return map;
  }, [dayTaskResults]);

  // ── Notification templates for preview ──────────────────────────────
  const { data: previewTemplates, isLoading: previewLoading } = useQuery(
    ["notif-templates-process", previewProcess],
    () =>
      NotificationTemplateService.getByProcess(previewProcess).then(
        (r) => r.data
      ),
    { enabled: !!previewProcess }
  );

  // ── Instance columns ───────────────────────────────────────────────────
  const instanceColumns = [
    {
      field: "idEmployee",
      headerName: "Employee ID",
      width: 200,
      renderCell: ({ value }) => (
        <code style={{ fontSize: "0.78rem" }}>{value}</code>
      ),
    },
    ...(isAnyEmployer
      ? [
          {
            field: "idEmployer",
            headerName: "Employer",
            width: 120,
            filterable: false,
          },
        ]
      : []),
    { field: "scheduleType", headerName: "Type", width: 240, filterable: false },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      filterable: false,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={value}
          color={INSTANCE_STATUS_COLORS[value] || "default"}
        />
      ),
    },
    {
      field: "anchorDate",
      headerName: "Anchor Date",
      width: 200,
      filterable: false,
      renderCell: ({ value }) =>
        value ? new Date(value).toLocaleString() : "—",
    },
    {
      field: "creationDate",
      headerName: "Created",
      width: 180,
      filterable: false,
      renderCell: ({ value }) =>
        value ? new Date(value).toLocaleString() : "—",
    },
    {
      field: "idCampaign",
      headerName: "Campaign ID",
      width: 300,
      filterable: false,
      renderCell: ({ value }) =>
        value ? (
          <code style={{ fontSize: "0.78rem" }}>{value}</code>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ),
    },
    {
      field: "idInstance",
      headerName: "Instance ID",
      width: 300,
      filterable: false,
      renderCell: ({ value }) => (
        <Tooltip title={value}>
          <code style={{ fontSize: "0.78rem" }}>{value}</code>
        </Tooltip>
      ),
    },
  ];

  // The grid only ever holds the current server page, so the built-in
  // column filter (Employee ID) must drive the server query instead of
  // filtering the loaded rows client-side.
  const onInstancesFilterChange = (filterModel) => {
    const idEmployeeFilter = filterModel.items?.find(
      (item) => item.field === "idEmployee" && item.value
    );
    setMemberIdSearch(idEmployeeFilter ? String(idEmployeeFilter.value) : "");
  };

  // ── Execution columns ──────────────────────────────────────────────────
  const executionColumns = [
    { field: "idExecution", headerName: "#", width: 70 },
    { field: "dayNumber", headerName: "Day", width: 80, type: "number" },
    {
      field: "idScheduleTask",
      headerName: "Task",
      width: 340,
      renderCell: ({ value }) => {
        const task = taskMap[value];
        if (!task) return <code style={{ fontSize: "0.78rem" }}>{value}</code>;
        const CHANNEL_COLORS = {
          SMS: "#0277bd",
          EMAIL: "#6a1b9a",
          VOICE: "#2e7d32",
          APP_MESSAGE: "#e65100",
        };
        return (
          <Tooltip
            title={
              <Box sx={{ fontSize: "0.8rem", lineHeight: 1.6 }}>
                <div>
                  <strong>Task ID:</strong> {value}
                </div>
                <div>
                  <strong>Process:</strong> {task.idProcess}
                </div>
                <div>
                  <strong>Hour:</strong> {formatEasternHour(task.hourUtc)}
                </div>
                {task.subject && (
                  <div>
                    <strong>Subject:</strong> {task.subject}
                  </div>
                )}
                {task.conditionJson && (
                  <div>
                    <strong>Condition:</strong> {task.conditionJson}
                  </div>
                )}
              </Box>
            }
            arrow
          >
            <Box display="flex" alignItems="center" gap={0.75}>
              <Chip
                size="small"
                label={task.taskType}
                sx={{
                  bgcolor: CHANNEL_COLORS[task.taskType] || "#555",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  height: 20,
                }}
              />
              <Typography variant="body2" noWrap sx={{ fontSize: "0.78rem" }}>
                {task.idProcess}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                sx={{
                  minWidth: 0,
                  px: 0.75,
                  py: 0,
                  fontSize: "0.7rem",
                  height: 20,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewProcess(task.idProcess);
                }}
              >
                Preview
              </Button>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "scheduledAt",
      headerName: "Scheduled / Executed",
      width: 200,
      renderCell: ({ row }) => {
        return (
          <Box lineHeight={1.4}>
            <Typography variant="body2" sx={{ fontSize: "0.78rem" }}>
              {formatEasternDateTime(row.scheduledAt)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatEasternDateTime(row.executedAt)}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={value}
          color={EXEC_STATUS_COLORS[value] || "default"}
        />
      ),
    },
    {
      field: "channelResponse",
      headerName: "Channel Response",
      flex: 1,
      minWidth: 200,
      renderCell: ({ value }) =>
        value ? (
          <code style={{ fontSize: "0.75rem" }}>{value}</code>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ),
    },
    {
      field: "errorMessage",
      headerName: "Error",
      flex: 1,
      minWidth: 200,
      renderCell: ({ value }) =>
        value ? (
          <Typography variant="body2" color="error" noWrap title={value}>
            {value}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Typography variant="h6" mb={2}>
        Communication Schedule Instances
      </Typography>

      {campaignIdFilter && (
        <Chip
          label={`Campaign: ${campaignIdFilter}`}
          onDelete={clearCampaignFilter}
          color="primary"
          variant="outlined"
          sx={{ mb: 2 }}
        />
      )}

      {/* Employer + member ID filters */}
      <Stack
        direction="row"
        spacing={2}
        mb={2}
        alignItems="center"
        flexWrap="wrap"
      >
        <Autocomplete
          options={employerOptions}
          value={selectedEmployer}
          onChange={(_, newVal) => setSelectedEmployer(newVal)}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          renderInput={(params) => (
            <TextField {...params} label="Filter by Employer" size="small" />
          )}
          sx={{ width: 350 }}
        />
        <TextField
          size="small"
          label="Search Member ID"
          placeholder="Employee ID…"
          value={memberIdSearch}
          onChange={(e) => setMemberIdSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 220 }}
        />
        {(selectedEmployer || memberIdSearch) && (
          <Button
            size="small"
            variant="text"
            onClick={() => {
              setSelectedEmployer(null);
              setMemberIdSearch("");
            }}
          >
            Clear
          </Button>
        )}
      </Stack>

      {/* Status filter */}
      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
        {ALL_INSTANCE_STATUSES.map((s) => (
          <Chip
            key={s}
            label={s === "ALL" ? `ALL (${instancesTotal})` : s}
            onClick={() => setStatusFilter(s)}
            variant={statusFilter === s ? "filled" : "outlined"}
            color={
              statusFilter === s
                ? s === "ALL"
                  ? "primary"
                  : INSTANCE_STATUS_COLORS[s] || "default"
                : "default"
            }
            size="small"
          />
        ))}
      </Stack>

      {/* Instances grid */}
      <DataGrid
        sx={{ height: 650, cursor: "pointer" }}
        rows={instances}
        columns={instanceColumns}
        getRowId={(r) => r.idInstance}
        loading={isLoading}
        paginationMode="server"
        rowCount={instancesTotal}
        page={paginationModel.page}
        pageSize={paginationModel.pageSize}
        onPageChange={(page) => setPaginationModel((pm) => ({ ...pm, page }))}
        onPageSizeChange={(pageSize) =>
          setPaginationModel({ page: 0, pageSize })
        }
        components={{ Toolbar: GridToolbar }}
        componentsProps={{ toolbar: { showQuickFilter: false } }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        filterMode="server"
        onFilterModelChange={onInstancesFilterChange}
        onRowClick={({ row }) => setSelectedInstance(row)}
      />

      {/* Executions dialog */}
      <Dialog
        open={!!selectedInstance}
        onClose={() => setSelectedInstance(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Executions
              </Typography>
              {selectedInstance && (
                <Typography variant="body2" color="text.secondary">
                  Employee: <strong>{selectedInstance.idEmployee}</strong>
                  &nbsp;·&nbsp;Type:{" "}
                  <strong>{selectedInstance.scheduleType}</strong>
                  &nbsp;·&nbsp;
                  <Chip
                    size="small"
                    label={selectedInstance.status}
                    color={
                      INSTANCE_STATUS_COLORS[selectedInstance.status] ||
                      "default"
                    }
                    sx={{ verticalAlign: "middle" }}
                  />
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => setSelectedInstance(null)}>
              ✕
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {execLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              autoHeight
              rows={executions || []}
              columns={executionColumns}
              getRowId={(r) => r.idExecution}
              rowsPerPageOptions={[10, 25, 50]}
              initialState={{
                pagination: { pageSize: 25 },
              }}
              disableSelectionOnClick
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ── Template preview dialog ── */}
      <Dialog
        open={!!previewProcess}
        onClose={() => setPreviewProcess(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Notification Template Preview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Process: <strong>{previewProcess}</strong>
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setPreviewProcess(null)}>
              ✕
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : !previewTemplates?.length ? (
            <Typography color="text.secondary" py={2}>
              No notification templates found for this process.
            </Typography>
          ) : (
            <Stack spacing={2} mt={0.5}>
              {previewTemplates.map((t) => (
                <Box
                  key={t.idNotificationTemplate}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Chip
                      size="small"
                      label={t.typeTemplate}
                      color={
                        t.typeTemplate === "SMS"
                          ? "primary"
                          : t.typeTemplate === "EMAIL"
                            ? "secondary"
                            : "default"
                      }
                    />
                    {t.lang && (
                      <Chip size="small" label={t.lang} variant="outlined" />
                    )}
                    <Chip
                      size="small"
                      label={t.status}
                      color={t.status === "ACTIVE" ? "success" : "default"}
                      variant="outlined"
                    />
                  </Box>
                  {t.idTemplate && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={0.5}
                    >
                      Template ID: <code>{t.idTemplate}</code>
                    </Typography>
                  )}
                  <Divider sx={{ my: 1 }} />
                  {t.template ? (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        bgcolor: "action.hover",
                        p: 1.5,
                        borderRadius: 1,
                      }}
                    >
                      {t.template}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      (No body — uses external template ID)
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
